import React, { useEffect, useState } from "react";
import {
  Grid,
  Paper,
  IconButton,
  InputBase,
  Button,
  Pagination,
  Box,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Checkbox,
  CardHeader,
  Avatar,
  Typography,
  Stack,
  Tooltip,
  Modal
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import PropTypes from 'prop-types';
import { visuallyHidden } from '@mui/utils';
import moment from 'moment';
import makeStyles from "@material-ui/styles/makeStyles";
import Loader from "react-js-loader";

import SearchIcon from '@mui/icons-material/Search';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import {ReactComponent as ViewIcon} from '../../assets/img/view.svg';
import {ReactComponent as EditIcon} from '../../assets/img/edit.svg';
import MoreOptionButton from "../../components/layout/MoreOptionButton";
import FilterAdmin from "../../components/layout/FilterAdmin";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { deleteUsers, searchUsers } from "../../redux/actions/user";
import config from "../../config/config";
import './style.css';

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

const headCells = [
  {
    id: 'member_name',
    numeric: false,
    disablePadding: true,
    label: 'Member Name',
  },
  {
    id: 'country',
    numeric: false,
    disablePadding: false,
    label: 'Country',
  },
  {
    id: 'booking_form_view_count',
    numeric: false,
    disablePadding: false,
    label: 'Views',
  },
  {
    id: 'booking_count',
    numeric: false,
    disablePadding: false,
    label: 'Books',
  },
  {
    id: 'perk_count',
    numeric: false,
    disablePadding: false,
    label: 'Perks',
  },
  {
    id: 'login_count',
    numeric: false,
    disablePadding: false,
    label: 'Logins',
  },
  {
    id: 'join_date',
    numeric: false,
    disablePadding: false,
    label: 'Join Date',
  },
  {
    id: 'expiry',
    numeric: false,
    disablePadding: false,
    label: 'Expiry',
  },
  {
    id: 'membership',
    numeric: false,
    disablePadding: false,
    label: 'Member',
  },
];

function EnhancedTableHead(props) {
  const theme = useTheme();
  const { onSelectAllClick, order, orderBy, numSelected, rowCount, onRequestSort } =
    props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        <TableCell padding="checkbox">
          <Checkbox
            color="primary"
            indeterminate={numSelected > 0 && numSelected < rowCount}
            checked={rowCount > 0 && numSelected === rowCount}
            onChange={onSelectAllClick}
            inputProps={{
              'aria-label': 'select all users',
            }}
          />
        </TableCell>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.numeric ? 'right' : 'left'}
            padding={headCell.disablePadding ? 'none' : 'normal'}
            sortDirection={orderBy === headCell.id ? order : false}
            sx={{
              fontSize: '12px',
	  		  padding: '0',
              color: theme.palette.mode ==="light" ? "#a1a5b7" : "#fff",
              '&:hover span': {
                color: theme.palette.mode ==="light" ? "#a1a5b7" : "#fff",
              },
            }}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
              sx={{ fontWeight: 600 }}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
        <TableCell
          align={'right'}
          padding={'normal'}
          sx={{
            fontSize: '12px',
            fontWeight: 600,
            color: theme.palette.mode ==="light" ? "#a1a5b7" : "#fff",
            '&:hover span': {
              color: theme.palette.mode ==="light" ? "#a1a5b7" : "#fff",
            },
          }}
        >
          Actions
        </TableCell>
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  numSelected: PropTypes.number.isRequired,
  onRequestSort: PropTypes.func.isRequired,
  onSelectAllClick: PropTypes.func.isRequired,
  order: PropTypes.oneOf(['asc', 'desc']).isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired,
};

const AdminTable = () => {
  const { loading, clientsData } = useSelector(state => ({
    loading: state.user.loading,
    clientsData: state.user.clientsData
  }));
  
  const dispatch = useDispatch();
  const theme = useTheme();
  const navigate = useNavigate();
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('');
  const [selected, setSelected] = useState([]);
  const [page, setPage] = useState(0);
  const [dense, setDense] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [cancel, setCancel] = useState(false);
  const [simpleDeleteUserId, setSimpleDeleteUserId] = useState([]);
  const [confirmDeleteText, setConfirmDeleteText] = useState('');
  const [filterOption, setFilterOption] = useState(-1);
  const [filteredClientsData, setFilteredClientsData] = useState([]);

  const useStyles = makeStyles({
    title: {
      color: theme.palette.mode === 'light' ? '#555600' : '#fff',
      fontSize: '0.86rem',
	  fontWeight: '500',
      lineHeight: '18px',
      '&:hover': {
        color: '#009ef7 !important',
      }
    },
    subheader: {
      color: theme.palette.mode === 'light' ? '#666300' : '#fff',
      fontSize: '0.7rem',
      lineHeight: '20px',
      '&:hover': {
        color: '#009ef7 !important',
      }
    }
  });
  const styles = useStyles();

  const handleClose = () => setOpen(false);

  const cancelDelete = () => {
    setConfirmDeleteText('');
    setOpen(false);
    setCancel(true);
    setSimpleDeleteUserId([]);
  }

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelected = clientsData.map((n) => n.id);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }
    setSelected(newSelected);
  };

  // const handleChangePage = (event, newPage) => {
  //   setPage(newPage);
  // };

  // const handleChangeRowsPerPage = (event) => {
  //   setRowsPerPage(parseInt(event.target.value, 10));
  //   setPage(0);
  // };

  // const handleChangeDense = (event) => {
  //   setDense(event.target.checked);
  // };

  const isSelected = (id) => selected.indexOf(id) !== -1;

  // Avoid a layout jump when reaching the last page with empty clientsData.
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - clientsData.length) : 0;

  const handleChangePage = (event, page) => {
    setPage(page - 1);
  }

  const handleGoDetailPage = (userId) => () => {
    navigate(`/superadmin/details/${userId}`);
  }

  const deleteSelectedUsers = () => {
    setConfirmDeleteText('');
    setConfirm(true);
    setOpen(false);
    dispatch(deleteUsers({ userIds: simpleDeleteUserId.length > 0 ? simpleDeleteUserId : selected }));
  }

  useEffect(() => {
    setSimpleDeleteUserId([]);
  }, [clientsData.length]);

  useEffect(() => {
    if (clientsData && clientsData.length) {
      if (filterOption > -1) {
        const tempFiltered = clientsData.filter((e) => e.membership == filterOption);
        setFilteredClientsData([ ...tempFiltered ]);
      } else {
        setFilteredClientsData([ ...clientsData ]);
      }
    }
  }, [clientsData, filterOption]);

  const handleSearchBooking = (event) => {
    dispatch(searchUsers({ keyword: event.target.value }));
  }

  return (
    <Grid
      container
      justifyContent='center'
      sx={{
        mt: { sm: 1, md: 1 },
        width: '100%',
        borderRadius: 2,
        paddingY: 3,
        backgroundColor: theme.palette.mode === 'light' ? '#fff' : 'rgba(25,25,30,0.5)'
      }}
    >
      <Grid
        item
        container
        direction='row'
        justifyContent='space-between'
        sx={{ width: '95%', pt: 1 }}
      >
        <Paper
          sx={{
            p: '0px 4px',
            display: 'flex',
            backgroundColor: theme.palette.mode === 'light' ? '#eef3f7' : 'transparent',
            boxShadow: 'none'
          }}
        >
          <IconButton sx={{ p: '0px 10px' }} aria-label="menu">
            <SearchIcon style={{ fill: theme.palette.mode === 'light' ? '#a1a5b7' : '#565674' }} />
          </IconButton>
          <InputBase
            placeholder="Search user"
            inputProps={{ sx: { fontSize: '0.9rem', fontWeight: 500 } }}
            onChange={handleSearchBooking}
            sx={{
              flex: 1,
              '& input::placeholder': {
                fontSize: '0.8rem',
                fontWeight: 500
              }
            }}
          />
        </Paper>
        {selected.length > 0 ?
          <Stack direction='row' sx={{ alignItems: 'center' }}>
            <Typography>{selected.length} selected</Typography>
            <Button
              variant="contained"
              color="error"
              sx={{ color: '#fff', fontWeight: 600, height: '2.5rem', textTransform: 'none', marginInlineStart: 3 }}
              onClick={() => setOpen(true)}
            >
              Delete Selected
            </Button>
          </Stack> :
          <FilterAdmin sx={{ padding: '10px 30px' }} onClick={(value) => setFilterOption(value)} />
        }

      </Grid>

      <Paper sx={{ width: '95%', mb: 2, mt: 2, boxShadow: 'none' }}>
        {loading ? (
          <Grid marginTop="50px" marginBottom="50px">
            <Loader type="bubble-loop" bgColor={"#2196f3"} color={'#2196f3'} size={100} />
          </Grid>
        ) : (
          <TableContainer>
            <Table
              sx={{ minWidth: 750 }}
              aria-labelledby="tableTitle"
            >
              <EnhancedTableHead
                numSelected={selected.length}
                order={order}
                orderBy={orderBy}
                onSelectAllClick={handleSelectAllClick}
                onRequestSort={handleRequestSort}
                rowCount={filteredClientsData.length}
              />
              <TableBody>
                {filteredClientsData.length > 0 && stableSort(filteredClientsData, getComparator(order, orderBy))
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row, index) => {
                    const isItemSelected = isSelected(row.id);
                    const labelId = `enhanced-table-checkbox-${index}`;

                    return (
                      <TableRow
                        hover
                        role="checkbox"
                        aria-checked={isItemSelected}
                        tabIndex={-1}
                        key={index}
                        selected={isItemSelected}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            color="primary"
                            checked={isItemSelected}
                            inputProps={{
                              'aria-labelledby': labelId,
                            }}
                            onClick={(event) => handleClick(event, row.id)}
                          />
                        </TableCell>
                        <TableCell
                          id={labelId}
                          scope="row"
                          padding="none"
                          sx={{ height: '70px' }}
                        >
                          <CardHeader
                            classes={{ title: styles.title, subheader: styles.subheader }}
                            avatar={row.avatar?.includes("https://gravatar.com/") ?
                              <Avatar sx={{ height: '3rem', width: '3rem' }} aria-label="recipe">
                                {row.firstName[0]}
                              </Avatar> :
                              <img
                                src={`${config.SERVER_BASE_URL}/${row.avatarThumbnail}`}
                                style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: '50%', transform: 'translate3d(0, 0, 1px)' }}
                                alt="avatar"
                              />
                            }
                            title={row.fullName}
                            subheader={row.email}
                            sx={{ padding: 0 }}
                          />
                        </TableCell>
                        <TableCell align="left" sx={{ fontSize: '0.75rem', padding: 0, color: theme.palette.mode ==="light" ? "#555400" : "#fff" }}>{row.country}</TableCell>
                        <TableCell align="left" sx={{ fontSize: '0.75rem', padding: 0, color: theme.palette.mode ==="light" ? "#555400" : "#fff" }}>{row.totalFormVisitCount}</TableCell>
                        <TableCell align="left" sx={{ fontSize: '0.75rem', padding: 0, color: theme.palette.mode ==="light" ? "#555400" : "#fff" }}>{row.bookCount}</TableCell>
                        <TableCell align="left" sx={{ fontSize: '0.75rem', padding: 0, color: theme.palette.mode ==="light" ? "#555400" : "#fff" }}>{row.perkCount}</TableCell>
                        <TableCell align="left" sx={{ fontSize: '0.75rem', padding: 0, color: theme.palette.mode ==="light" ? "#555400" : "#fff" }}>{row.loginCount}</TableCell>
                        <TableCell align="left" sx={{ fontSize: '0.75rem', padding: 0, color: theme.palette.mode ==="light" ? "#555400" : "#fff" }}>{moment(row.createdAt).format('D MMM YYYY')}</TableCell>
                        <TableCell align="left" sx={{ fontSize: '0.75rem', padding: 0, color: theme.palette.mode ==="light" ? "#555400" : "#fff" }}>{moment(row.expireDate).format('D MMM YYYY')}</TableCell>
                        <TableCell align="left" sx={{ fontSize: '0.75rem', padding: 0, color: theme.palette.mode ==="light" ? "#555400" : "#fff" }}>
                          {row.membership === 0 ?
                            <Box className="booking-detail-type" sx={{ color: '#FFC700', backgroundColor: theme.palette.mode ==="light" ? "#FFF8DD" : "#392F28" }}>Trial</Box> :
                            row.membership === 1 ?
                              <Box className="booking-detail-type" sx={{ color: '#F1416C', backgroundColor: theme.palette.mode ==="light" ? "#FFF5F8" : "#3A2434" }}>Expired</Box> :
                              <Box className="booking-detail-type" sx={{ color: '#50CD89', backgroundColor: theme.palette.mode === 'light' ? "#E8FFF3" : "#1C3238" }}>Pro</Box>
                          }
                        </TableCell>
                        <TableCell align="left" sx={{ fontSize: '0.8rem', paddingRight: '4px' }}>
                          <Stack direction='row' justifyContent="flex-end">
                            <Tooltip title="View" placement="top" enterTouchDelay={0}>
                              <IconButton
                                sx={{
                                  color: '#a1a5b7',
                                  "&:hover": {
                                    backgroundColor: theme.palette.mode === 'light' ? '#f1faff' : '#212e48',
                                    borderRadius: '6px',
                                  },
                                  "&:hover svg": {
                                    color: '#009ef7',
                                  },
                                }}
                                onClick={handleGoDetailPage(row._id)}
                              >
                                <ViewIcon style={{ width: '20px', height: '20px' }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Edit" placement="top" enterTouchDelay={0}>
                              <IconButton
                                sx={{
                                  color: '#a1a5b7',
                                  "&:hover": {
                                    backgroundColor: theme.palette.mode === 'light' ? '#f1faff' : '#212e48',
                                    borderRadius: '6px',
                                  },
                                  "&:hover svg": {
                                    color: '#009ef7',
                                  },
                                }}
                                onClick={() => navigate('/superadmin/edit/' + row._id)}
                              >
                                <EditIcon style={{ width: '20px', height: '20px' }} />
                              </IconButton>
                            </Tooltip>
                            <MoreOptionButton
                              onClick={() => {
                                setSimpleDeleteUserId([row.id]);
                                setOpen(true);
                              }}
                            />
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                {emptyRows > 0 && (
                  <TableRow
                    style={{
                      height: (dense ? 33 : 53) * emptyRows,
                    }}
                  >
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {/* <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={clientsData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        /> */}
      </Paper>

      <Grid sx={{ mt: 1, width: '95%' }} container justifyContent='space-between' alignItems="center">
        <Typography sx={{ fontSize: '12px', textAlign: 'center' }}>
          Displaying {page * rowsPerPage + 1} - {Math.min((page + 1) * rowsPerPage, filteredClientsData.length)} of {filteredClientsData.length} entries
        </Typography>
        <Pagination
          count={Math.ceil(filteredClientsData.length / rowsPerPage)}
          shape="rounded"
          color="primary"
          onChange={handleChangePage}
          sx={{
            "& .MuiPaginationItem-root": {
              '&.Mui-selected': {
                color: '#fff'
              }
            }
          }}
        />
      </Grid>

      <Modal
        open={open}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
          }}
        >
          <Grid sx={{ width: '80%', margin: 'auto' }} className="text-center">
            <Grid container justifyContent='center' alignItems='center' className="swal-question swal-close" sx={{ borderColor: "#FFC700" }}>
              <Grid className="swal-i-content" sx={{ fontSize: '2.75em', color: '#FFC700' }}>!</Grid>
            </Grid>
            <Typography id="modal-modal-description" sx={{ mt: 2, textAlign: 'center' }}>
              Are you sure you want to delete selected customers?
            </Typography>
            <Typography id="modal-modal-description" sx={{ mt: 3, textAlign: 'center' }}>
              Please type DELETE below to confirm
            </Typography>
            <TextField
              variant="standard"
              type="text"
              placeholder='Type here...'
              name='password'
              value={confirmDeleteText}
              onChange={(e) => setConfirmDeleteText(e.target.value)}
              InputProps={{
                disableUnderline: true,
                style: {
                  padding: '8px 20px',
                  borderRadius: '6px',
                  color: '#5e6278',
                  fontSize: '0.8rem',
                  fontWeight: 600,
                },
              }}
              sx={{ mt: 1 }}
            />
            <Grid item container direction='row' justifyContent='center' alignItems='center' display="flex" sx={{ mt: 3 }}>
              <Button
                disabled={confirmDeleteText !== 'DELETE'}
                color="error"
                variant="contained"
                sx={{
                  fontSize: '0.8rem',
                  fontWeight: 600,
                  textTransform: 'none'
                }}
                onClick={deleteSelectedUsers}
              >
                Yes, delete!
              </Button>
              <Button color="success" variant="contained" sx={{ marginInlineStart: 3, fontSize: '0.8rem', fontWeight: 600, textTransform: 'none', color: '#fff' }} onClick={cancelDelete}>No, cancel</Button>
            </Grid>
          </Grid>
        </Box>
      </Modal>

      <Modal
        open={confirm}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
          }}
        >
          <Grid sx={{ width: '80%', margin: 'auto' }} className="text-center">
            <Grid container justifyContent='center' alignItems='center' className="swal-question swal-close" sx={{ borderColor: "#50CD89" }}>
              <Grid className="swal-x-content" sx={{ fontSize: '2.75em', color: '#50CD89' }}>&#10004;</Grid>
            </Grid>
            <Typography id="modal-modal-description" sx={{ mt: 2, textAlign: 'center' }}>
              You have deleted all selected customers!
            </Typography>
            <Grid item container direction='row' justifyContent='center' alignItems='center' display="flex" sx={{ mt: 3 }}>
              <Button color="primary" variant="contained" sx={{ fontSize: '0.8rem', fontWeight: 600, textTransform: 'none', color: '#fff' }} onClick={() => setConfirm(false)}>Ok, got it!</Button>
            </Grid>
          </Grid>
        </Box>
      </Modal>

      <Modal
        open={cancel}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
          }}
        >
          <Grid sx={{ width: '80%', margin: 'auto' }} className="text-center">
            <Grid container justifyContent='center' alignItems='center' className="swal-question swal-close" sx={{ borderColor: "#F1416C" }}>
              <Grid className="swal-x-content" sx={{ fontSize: '2.75em', color: '#F1416C' }}>&#x2715;</Grid>
            </Grid>
            <Typography id="modal-modal-description" sx={{ mt: 2, textAlign: 'center' }}>
              Selected customers were not deleted.
            </Typography>
            <Grid item container direction='row' justifyContent='center' alignItems='center' display="flex" sx={{ mt: 3 }}>
              <Button color="primary" variant="contained" sx={{ fontSize: '0.8rem', fontWeight: 600, textTransform: 'none', color: '#fff' }} onClick={() => setCancel(false)}>Ok, got it!</Button>
            </Grid>
          </Grid>
        </Box>
      </Modal>

    </Grid>
  );
}

export default AdminTable;
